import {
  memo, useCallback, useMemo, useState,
} from 'react';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import _, { isEmpty } from 'lodash';
import { useAnalytics } from 'use-analytics';
import clsx from 'clsx';
import InfoIcon from '@material-ui/icons/Info';
import NumberFormat from 'react-number-format';
import getTripConfigurationItems from '../../../helpers/getTripConfigurationItems';
import ExpandableRow from '../../ExpandableRow';
import {
  BookingStatus, MaxRentalCarsInTrip, PageLinks, TravelOptionTab, TravelTypes, SearchModes,
  UserRolePermissions,
  AgencyPlanningType,
  MaxHotelsInTrip,
} from '../../../constants';
import Button from '../../Button';
import ToolTip from '../../ToolTip';
import formatTrip from '../../../helpers/formatTrip';
import computeCruiseTotals from '../../../helpers/computeCruiseTotals';
import ChangeBookingConfirmModal from './ChangeBookingConfirmModal';
import CancelBookingConfirmModal from './CancelBookingConfirmModal';
import BookingActions from './BookingActions';
import getTripItineraries from '../../../helpers/getTripItineraries';
import { checkDoShowTripConfirmationPricing, checkSitePermission } from '../../../store/slices/user';
import ChangeInfoModal from './ChangeInfoModal';
import checkDoAllowChangeOrCancelFlight from '../../../helpers/checkDoAllowChangeOrCancelFlight';
import CancelFlightInfoModal from './CancelFlightInfoModel';
import LoginRequestModal from '../../LoginRequestModal';
import getCurrentPageURL from '../../../helpers/getCurrentPageURL';
import getCarrierFullName from '../../../helpers/getCarrierFullName';
import Label from '../../Label';
import currencySymbol from '../../../helpers/currencySymbol';
import checkDoEnableTripConfigDecoupling from '../../../helpers/selectors/checkDoEnableTripConfigDecoupling';
import ConfirmModal from '../../ConfirmModal';

const ViewTripConfigurationItems = ({
  tripId, isTripSearch, cruiseTotals, updatedItineraries,
  onRemoveClick, doShowConfirmedTrip, onTabChange,
  doShowAgentCommission, totalPriceBreakdown,
}) => {
  const { track } = useAnalytics();
  const router = useRouter();
  const { test } = router.query;
  const canChangeBooking = useSelector(checkSitePermission(UserRolePermissions.canChangeBooking));
  const canCancelBooking = useSelector(checkSitePermission(UserRolePermissions.canCancelBooking));
  const doEnableTripConfigDecoupling = useSelector(checkDoEnableTripConfigDecoupling);
  const isConfirmationPage = router.route.indexOf('confirmation') > -1;
  const isResultsPage = router.pathname === PageLinks.Home.url;
  let cruiseTotalsToShow = cruiseTotals;
  const selectedSearchMode = useSelector((state) => state.tripSearch.selectedSearchMode);
  const canHold = useSelector(checkSitePermission(UserRolePermissions.canHold));
  const {
    tripConfirmation, selectedTrips, passengers, agencyPlanningFees, doChargeAgencyPlanningFees,
    agencyPlanningFeesKeptByAgency, doSplitServiceFees, agencyPlanningType,
  } = useSelector((store) => store.trip);
  const { isLoggedIn, travelAgency, email: loggedInUserEmail } = useSelector((store) => store.user);
  const { disableMultipleRoomBookings } = (travelAgency
    && travelAgency.settings && travelAgency.settings) || {};
  const doShowPricing = useSelector(checkDoShowTripConfirmationPricing);
  const priceBreakdowns = useSelector((state) => state.cruise.priceBreakdowns);
  const hasConfirmation = tripConfirmation && (tripConfirmation.bookingResponses || []).length > 0;
  const doAllowUpdateTrip = !hasConfirmation && isLoggedIn;
  const doShowBookingActions = !isConfirmationPage && !doShowConfirmedTrip && canHold;
  const areAllPassengerDetailsProvided = tripConfirmation && tripConfirmation.allowedTripChanges
    && tripConfirmation.allowedTripChanges.areAllPassengerDetailsProvided;
  const allowedUpdateItinerary = tripConfirmation && tripConfirmation.allowedTripChanges
      && tripConfirmation.allowedTripChanges.updateItinerary;

  let trip;
  if (doShowConfirmedTrip && hasConfirmation) {
    trip = formatTrip({ trip: tripConfirmation && tripConfirmation.trip, tripConfirmation });
    cruiseTotalsToShow = computeCruiseTotals({
      trip,
      priceBreakdowns,
      passengers,
      bookingResponses: tripConfirmation && tripConfirmation.bookingResponses,
    }) || {};
  } else {
    trip = selectedTrips[tripId];
  }

  const carrierFullName = getCarrierFullName(trip && trip.flightItinerary);

  const getStatus = (li) => li?.status || li?.bookingResponses?.[0]?.status;

  const doShowCancelButton = (li) => {
    const status = getStatus(li);

    return isConfirmationPage
    && hasConfirmation
    && li.key !== 'flightSeats'
    && li.key !== 'travelAgencyPlanningFee'
    && status !== BookingStatus.cancelled
    && status !== BookingStatus.pending
    && status !== BookingStatus.failed;
  };

  const handleGoToPage = ((pageName, segmentId, travelOptionTab) => {
    if (!hasConfirmation) {
      if (onTabChange) {
        onTabChange(travelOptionTab);
      }
      return;
    }
    const query = {
      tripId,
    };
    if (test === 'true') {
      query.test = true;
    }
    if (segmentId) {
      query.segment = segmentId;
    }
    router.push({
      pathname: `/${pageName}`,
      query,
    });
    track('tripConfiguration.handleGoToPage', {
      tripId, pageName, loggedInUserEmail, page: getCurrentPageURL(),
    });
  });

  const [doShowChangeModal, setDoShowChangeModal] = useState(false);
  const [doShowCancelLoginModal, setDoShowCancelLoginModal] = useState(false);
  const [selectedMode, setMode] = useState();
  const [doShowCancelFlightModal, setDoShowCancelFlightModal] = useState(false);
  const [doShowConfirmModal, setDoShowConfirmModal] = useState(false);
  const [doShowCancelModal, setDoShowCancelModal] = useState(false);
  const [itineraryForModal, setItineraryForModal] = useState();
  const [selectedPageName, setSelectedPageName] = useState();
  const [isRebookingFailedSegment, setIsRebookingFailedSegment] = useState();

  const handleGoToPageWithConfirmation = ((pageName, itinerary, travelOptionTab) => {
    if (!hasConfirmation) {
      if (onTabChange) {
        onTabChange(travelOptionTab);
      }
      return;
    }
    setDoShowConfirmModal(true);
    setSelectedPageName(pageName);
    setItineraryForModal(itinerary);
    if (itinerary.mode === TravelTypes.hotel
      && itinerary.status === BookingStatus.failed) {
      setIsRebookingFailedSegment(true);
    }
    track('tripConfiguration.handleGoToPageWithConfirmation', {
      itineraryMode: itinerary.mode, pageName, loggedInUserEmail, page: getCurrentPageURL(),
    });
  });

  const handleCancelBookedItinerary = ((itinerary) => {
    setDoShowCancelModal(true);
    setItineraryForModal(itinerary);
    track('tripConfiguration.handleCancelBookedItinerary', { itineraryMode: itinerary.mode, loggedInUserEmail, page: getCurrentPageURL() });
  });

  const handleOk = (() => {
    setDoShowConfirmModal(false);
    let id = itineraryForModal
      && itineraryForModal.mode === TravelTypes.carrental && itineraryForModal.id;

    if (itineraryForModal
        && itineraryForModal.mode === TravelTypes.hotel) {
      id = itineraryForModal.tripSegmentId;
    }
    handleGoToPage(selectedPageName, id > 0 ? id : undefined);
    setIsRebookingFailedSegment(false);
  });

  const handleOkCancellation = (() => {
    setDoShowCancelModal(false);
  });

  const handleCancel = (() => {
    setDoShowChangeModal(false);
    setDoShowCancelFlightModal(false);
    setDoShowConfirmModal(false);
    setDoShowCancelModal(false);
    setSelectedPageName(null);
    setIsRebookingFailedSegment(false);
  });

  const getPriceSubtitleByItinerary = (itinerary, li, doIncludeRateInPropertyCurrency) => {
    if (itinerary && itinerary.mode === TravelTypes.carrental
        && !itinerary.priceInfo.isPrepaidRate) {
      return 'Pay at Pick-up';
    }
    if (doIncludeRateInPropertyCurrency && itinerary && itinerary.mode === TravelTypes.hotel
      && itinerary.selectedProduct && itinerary.selectedProduct.roomPrice
      && itinerary.selectedProduct.roomPrice.propertyCurrencyCode
      && itinerary.selectedProduct.roomPrice.currencyCode
      !== itinerary.selectedProduct.roomPrice.propertyCurrencyCode
      && itinerary.selectedProduct.roomPrice.propertyCurrencyRate > 0
    ) {
      return (
        <Label
          text={(
            <span>
              <NumberFormat
                value={itinerary.selectedProduct.roomPrice.propertyCurrencyRate}
                prefix={currencySymbol(itinerary.selectedProduct.roomPrice.propertyCurrencyCode)}
                displayType="text"
                thousandSeparator
                fixedDecimalScale="true"
              />
              <InfoIcon className="-mt-1" style={{ fontSize: '1rem', marginLeft: '1px' }} />
            </span>
          )}
          inline
          descriptionOnHover={(
            <span>
              The property will charge you the full amount of
              {' '}
              <NumberFormat
                value={li.price}
                prefix={currencySymbol(li.currencyCode)}
                displayType="text"
                thousandSeparator
                fixedDecimalScale="true"
                decimalScale="2"
              />
              {' '}
              in
              {' '}
              {itinerary.selectedProduct.roomPrice.propertyCurrencyCode}
              {' '}
              (
              <NumberFormat
                value={itinerary.selectedProduct.roomPrice.propertyCurrencyRate}
                prefix={currencySymbol(itinerary.selectedProduct.roomPrice.propertyCurrencyCode)}
                displayType="text"
                thousandSeparator
                fixedDecimalScale="true"
                decimalScale="2"
              />
              ). Your card issuer may charge you a conversion fee.
            </span>
          )}
        />
      );
    }
    return null;
  };

  const doShowAddLegLink = useCallback((mode) => {
    const enabledTransportModes = (travelAgency && travelAgency.enabledTransportModes) || [];
    const modesNotallowedToAddToBooking = (travelAgency
      && travelAgency.modesNotallowedToAddToBooking) || [];

    if (modesNotallowedToAddToBooking.length && !isResultsPage
      && modesNotallowedToAddToBooking.indexOf(mode) > -1) return false;
    if (enabledTransportModes.length) {
      if (mode === TravelTypes.hotel && enabledTransportModes.indexOf(+SearchModes.HOTEL) === -1) {
        return false;
      }
      if (mode === TravelTypes.flight
        && enabledTransportModes.indexOf(+SearchModes.FLIGHT) === -1) {
        return false;
      }
      if (mode === TravelTypes.cruise && enabledTransportModes
        .indexOf(SearchModes.CRUISE_TRIP) === -1) {
        return false;
      }
      if (mode === TravelTypes.carrental
        && enabledTransportModes.indexOf(+SearchModes.CAR) === -1) {
        return false;
      }
    }
    let maxRentalCarsInTrip = MaxRentalCarsInTrip;
    if (!hasConfirmation) {
      if (mode === TravelTypes.cruise
        && selectedSearchMode !== SearchModes.CRUISE_TRIP) return false;
      if (mode === TravelTypes.carrental
          && selectedSearchMode === SearchModes.CAR) maxRentalCarsInTrip = 1;
    }

    if (!isResultsPage && !areAllPassengerDetailsProvided && mode === TravelTypes.flight) {
      return false;
    }

    const tripSegments = hasConfirmation
      ? tripConfirmation.trip.tripSegments : getTripItineraries(trip);
    if (mode === TravelTypes.carrental) {
      return (_.filter(tripSegments, (ts) => ts.mode
      === TravelTypes.carrental) || []).length < maxRentalCarsInTrip;
    } if (mode === TravelTypes.hotel) {
      return (_.filter(tripSegments, (ts) => ts.mode
      === TravelTypes.hotel) || []).length < (disableMultipleRoomBookings ? 1 : MaxHotelsInTrip);
    }
    return !_.find(tripSegments, (ts) => ts.mode === mode);
  }, [areAllPassengerDetailsProvided, hasConfirmation, isResultsPage, selectedSearchMode,
    travelAgency, trip, tripConfirmation, disableMultipleRoomBookings]);

  const shouldShowAddHotel = useMemo(() => doShowAddLegLink(TravelTypes.hotel)
    && (isConfirmationPage || isEmpty(selectedTrips?.[tripId]?.hotelItineraries)
    || doEnableTripConfigDecoupling),
  [doShowAddLegLink, isConfirmationPage, selectedTrips, tripId, doEnableTripConfigDecoupling]);

  const passengersCount = useMemo(() => Object.keys(passengers || {}).length, [passengers]);
  const totalAgencyPlanningFees = useMemo(() => {
    if (!isLoggedIn && travelAgency && !travelAgency.defaultDoShowPricingToAnonymous) return 0;
    if (doShowConfirmedTrip) {
      if (tripConfirmation.agencyPlanningType === AgencyPlanningType.AgencyPlanningFeesPerPassenger
        && passengersCount > 0) {
        return tripConfirmation.agencyPlanningFees * passengersCount;
      }
      return tripConfirmation.agencyPlanningFees;
    }
    if (doChargeAgencyPlanningFees) {
      if (agencyPlanningType === AgencyPlanningType.AgencyPlanningFeesPerPassenger
        && passengersCount > 0) {
        return agencyPlanningFees * passengersCount;
      }
      return agencyPlanningFees;
    }
    return 0;
  }, [agencyPlanningFees, agencyPlanningType, doChargeAgencyPlanningFees, doShowConfirmedTrip,
    isLoggedIn, passengersCount, travelAgency, tripConfirmation]);

  const getAgencyPlanningFeesKeptByAgency = () => {
    if (!isLoggedIn && travelAgency && !travelAgency.defaultDoShowPricingToAnonymous) return 0;
    if (doShowConfirmedTrip) {
      return tripConfirmation.agencyPlanningFeesKeptByAgency;
    }
    return doChargeAgencyPlanningFees && doSplitServiceFees ? agencyPlanningFeesKeptByAgency : 0;
  };

  const agencyPlanningFeeLabel = travelAgency && travelAgency.settings
  && travelAgency.settings.terms && travelAgency.settings.terms.agencyPlanningFeeLabel;
  return (
    <div className="grid divide-y gap-2">
      {trip
    && getTripConfigurationItems(trip, cruiseTotalsToShow,
      doShowConfirmedTrip ? tripConfirmation : null,
      updatedItineraries, passengers, isTripSearch,
      totalAgencyPlanningFees, getAgencyPlanningFeesKeptByAgency(), doShowAgentCommission,
      agencyPlanningFeeLabel, totalPriceBreakdown)
      .map((li, index, items) => (
        <ExpandableRow
          key={li.key + li.id}
          title={(
            <>
              {li.title}
              {!li.doHideRemoveBtn
               && (isTripSearch || (!doShowConfirmedTrip && doAllowUpdateTrip && _.filter(items,
                 (t) => t.mode !== undefined).length > 1 && li.mode !== undefined)) && (
                 <button
                   type="button"
                   className="text-xs xl:text-sm ml-1 text-right leading-5 text-theme underline focus:outline-none"
                   onClick={() => {
                     onRemoveClick(li.key, index, li.mode, li.id);
                   }}
                 >
                   Remove
                 </button>
              )}
            </>
          )}
          subtitle={(
            <>
              {li.byline
                && (
                  <div>
                    {li.byline}
                  </div>
                )}
              {li.confirmationCode
                && (
                  <div>
                    Confirmation:
                    {' '}
                    {li.confirmationCode}
                  </div>
                )}
              {li.sabreRecordLocator && (
              <div>
                Record Locator:
                {' '}
                {li.sabreRecordLocator}
              </div>
              )}
              {li.orderId && (
              <div>
                Order Id:
                {' '}
                {li.orderId}
              </div>
              )}
              {li.providerConfirmation
                && (
                  <div className="lg:whitespace-no-wrap">
                    {li.providerConfirmation}
                  </div>
                )}
              {li.status && (
                <>
                  <div
                    className={clsx(
                      'flex items-center',
                      li.status === BookingStatus.pending || li.status === BookingStatus.failed
                        ? 'bg-red-200'
                        : '',
                    )}
                  >
                    <div className="flex flex-nowrap lg:whitespace-no-wrap">
                      <span className="whitespace-no-wrap">
                        {`Status: ${(li.statusText || li.status)?.replace(/\s*\(HK\)/g, '')}`}
                        {(li.mode === TravelTypes.flight
                          && li.status !== BookingStatus.pending
                          && li.status !== BookingStatus.failed)
                          && ' - '}
                      </span>
                    </div>
                    <div className="flex flex-wrap">
                      {li.mode === TravelTypes.flight
                      && li.status !== BookingStatus.pending
                      && li.status !== BookingStatus.failed && (
                      <span className="flex items-center whitespace-no-wrap">
                        {(() => {
                          if (li.status === BookingStatus.cancelled) {
                            return <span className="ml-1 text-red-500 font-bold">Not Ticketed</span>;
                          }
                          if (li.isTicketed) {
                            return <span className="ml-1 text-green-700 font-bold">Ticketed</span>;
                          }
                          return (
                            <>
                              <span className="ml-1 text-red-500 font-bold">Ticketing Pending</span>
                              <ToolTip
                                disableHoverListener={false}
                                showCloseIcon={false}
                                variant="light"
                                title={(
                                  <span className="whitespace-pre-line text-xs">
                                    {li.isPaymentMethodAdded ? 'Ticketing required to finalize your booking.' : 'Ticketing is required to finalize your booking after a payment method has been added.'}
                                  </span>
                                )}
                              >
                                <InfoIcon className="ml-1 cursor-pointer text-red-500 font-bold" style={{ fontSize: '1rem' }} />
                              </ToolTip>
                            </>
                          );
                        })()}
                      </span>
                      )}
                    </div>
                  </div>
                  {li.failureReason && (
                    <div className="italic text-sm mb-1 text-red-500">{li.failureReason}</div>
                  )}
                </>
              )}
              {li.ticketNumbers && (
                <div>
                  Ticket #:
                  {' '}
                  {li.ticketNumbers}
                </div>
              )}
              {li.referenceNumbers && (
                <div>
                  Reference #:
                  {' '}
                  {li.referenceNumbers}
                </div>
              )}
              {li.emdNumbers && (
                <div>
                  Miscellaneous Document (EMD) #:
                  {' '}
                  {li.emdNumbers}
                </div>
              )}
              {li.cancellationReference
                && (
                  <div className="lg:whitespace-no-wrap">
                    {li.cancellationReference}
                  </div>
                )}
              {li.bookingResponses && li.bookingResponses.map((r, bookingIndex) => (
                <div className="grid grid-cols-3" key={`${bookingIndex.toString()}`}>
                  {li.bookingResponses.length > 1 && (
                  <div className="font-bold">
                    Stateroom
                      {' '}
                    {r.roomIndex + 1}
                    :
                  </div>
                  )}
                  <div className="col-span-2">
                    {r.providerConfirmation
                    && (
                    <div className="lg:whitespace-no-wrap">
                      {r.providerConfirmation}
                    </div>
                    )}
                    <div className="lg:whitespace-no-wrap">
                      {li.mode === TravelTypes.cruise ? 'Booking Reference:' : 'Confirmation:'}
                      {' '}
                      {r.confirmationCode}
                    </div>
                    {r.sabreRecordLocator && (
                      <div>
                        Record Locator:
                        {' '}
                        {r.sabreRecordLocator}
                      </div>
                    )}
                    {r.orderId && (
                      <div>
                        Order Id:
                        {' '}
                        {r.orderId}
                      </div>
                    )}
                    <div>
                      Status:
                      {' '}
                      {r.statusText || r.status}
                    </div>
                    {r.cancellationReference
                      && (
                        <div className="lg:whitespace-no-wrap">
                          {r.cancellationReference}
                        </div>
                      )}
                  </div>
                </div>
              ))}
              {((isConfirmationPage && hasConfirmation) || isResultsPage)
                && !li.doHideCancelButton && (
                <div>
                  {doShowCancelButton(li)
                  && (
                    <Button
                      variant={Button.buttonVariants.LINK}
                      onClick={() => {
                        if (!canCancelBooking) {
                          setDoShowCancelLoginModal(true);
                          return;
                        }
                        if (li.mode === TravelTypes.flight
                          && (!_.isEmpty(li.bookingResponse && li.bookingResponse.orderId)
                           || !checkDoAllowChangeOrCancelFlight(li.bookingResponse,
                             travelAgency?.doAllowCancellingTicketedFlight))) {
                          setDoShowCancelFlightModal(true);
                        } else {
                          handleCancelBookedItinerary(li);
                        }
                      }}
                      className="mr-2"
                    >
                      <u>Cancel</u>
                    </Button>
                  )}
                  {index === 0 && (
                    <>
                      {doShowAddLegLink(TravelTypes.flight)
                      && (
                        <Button
                          variant={Button.buttonVariants.LINK}
                          onClick={() => handleGoToPage(PageLinks.AddFlight.url,
                            null, TravelOptionTab.FLIGHT)}
                          className="mr-2"
                        >
                          <u>Add Flight</u>
                        </Button>
                      )}
                      {doShowAddLegLink(TravelTypes.carrental)
                      && (
                        <Button
                          variant={Button.buttonVariants.LINK}
                          onClick={() => handleGoToPage(PageLinks.AddCar.url,
                            null, TravelOptionTab.GROUND)}
                          className="mr-2"
                        >
                          <u>Add Car</u>
                        </Button>
                      )}
                      {shouldShowAddHotel
                        && (
                        <Button
                          variant={Button.buttonVariants.LINK}
                          onClick={() => handleGoToPage(PageLinks.AddHotel.url,
                            null, TravelOptionTab.HOTEL)}
                          className="mr-2"
                        >
                          <u>Add Hotel</u>
                        </Button>
                        )}
                      {doShowAddLegLink(TravelTypes.cruise)
                        && (
                        <Button
                          variant={Button.buttonVariants.LINK}
                          onClick={() => handleGoToPage(PageLinks.AddCruise.url,
                            null, TravelOptionTab.CRUISE)}
                          className="mr-2"
                        >
                          <u>Add Cruise</u>
                        </Button>
                        )}
                    </>
                  )}
                  {li.mode === TravelTypes.hotel && allowedUpdateItinerary && (
                  <Button
                    variant={Button.buttonVariants.LINK}
                    onClick={() => (!canChangeBooking
                      ? (() => { setDoShowChangeModal(true); setMode(li.mode); })()
                      : handleGoToPageWithConfirmation(
                        PageLinks.ChangeHotel.url, li, TravelOptionTab.HOTEL,
                      )
                    )}
                  >
                    <u>{li.changeRoomButtonText || 'Change Hotel/Room'}</u>
                  </Button>
                  )}
                  {li.mode === TravelTypes.cruise && allowedUpdateItinerary && (
                  <Button
                    variant={Button.buttonVariants.LINK}
                    onClick={() => handleGoToPageWithConfirmation(PageLinks.ChangeCruise.url, li,
                      TravelOptionTab.CRUISE)}
                  >
                    <u>Change Cruise</u>
                  </Button>
                  )}
                  {li.mode === TravelTypes.flight && allowedUpdateItinerary && (
                  <Button
                    variant={Button.buttonVariants.LINK}
                    onClick={() => (!checkDoAllowChangeOrCancelFlight(li.bookingResponse)
                      || !canChangeBooking
                      ? (() => { setDoShowChangeModal(true); setMode(li.mode); })()
                      : handleGoToPageWithConfirmation(PageLinks.ChangeFlight.url, li,
                        TravelOptionTab.FLIGHT))}
                  >
                    <u>Change Flight</u>
                  </Button>
                  )}
                  {li.mode === TravelTypes.carrental && allowedUpdateItinerary && (
                  <Button
                    variant={Button.buttonVariants.LINK}
                    onClick={() => (!canChangeBooking
                      ? (() => { setDoShowChangeModal(true); setMode(li.mode); })()
                      : handleGoToPageWithConfirmation(
                        PageLinks.ChangeCar.url, li, TravelOptionTab.GROUND,
                      ))}
                  >
                    <u>Change Car</u>
                  </Button>
                  )}
                  {li.mode === TravelTypes.tour && allowedUpdateItinerary && (
                    <Button
                      variant={Button.buttonVariants.LINK}
                      onClick={() => handleGoToPageWithConfirmation(PageLinks.ChangeTour.url, li,
                        TravelOptionTab.TOUR)}
                    >
                      <u>Change Tour</u>
                    </Button>
                  )}
                </div>
              )}
              {doShowBookingActions && li.itinerary
                ? (
                  <BookingActions
                    itinerary={li.itinerary}
                    tripId={tripId}
                  />
                ) : <div className="my-3" />}
            </>
          )}
          price={li.price}
          currencyCode={li.currencyCode}
          priceSubtitle={getPriceSubtitleByItinerary(li.itinerary, li,
            travelAgency && travelAgency.doIncludeRateInPropertyCurrency)}
          oldPrice={li.oldPrice}
          doShowPricing={doShowPricing}
          bookingwarning={li.bookingResponse && li.status !== BookingStatus.cancelled
            && li.status !== BookingStatus.pending
            && li.status !== BookingStatus.failed
            && li.mode === TravelTypes.flight && !li.bookingResponse.isPaymentMethodAdded
            && li.lastDateToPurchaseUtc ? (
              <div className="bg-red-600 rounded-md  p-2 text-white">
                <b>Note:</b>
                {' '}
                Last ticketing date is
                {' '}
                {new Date(li.lastDateToPurchaseUtc).toLocaleString('en-US', {
                  year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
                })}
                .
                If payment card information is not provided by then, this booking will
                be automatically cancelled.
              </div>
            ) : <></>}
          pnrAAFacts={li.bookingResponse && li.status !== BookingStatus.cancelled
            && li.status !== BookingStatus.pending
            && li.status !== BookingStatus.failed
            && li.mode === TravelTypes.flight
            && li.pnrAAFacts && (
            <div className="bg-red-600 rounded-md  p-2 mt-1 text-white">
              <b>Airline-Specific Info (AA FACTS):</b>
              <ul className="list-none space-y-1 mt-1">
                {li.pnrAAFacts.map((fact) => (
                  <li key={li.key}>{fact}</li>
                ))}
              </ul>
            </div>
          )}
        >
          {li.info}
        </ExpandableRow>
      ))}
      {doShowConfirmModal && itineraryForModal
        && (itineraryForModal.mode === TravelTypes.hotel && isRebookingFailedSegment
          ? (
            <ConfirmModal
              onCancel={handleCancel}
              onConfirm={handleOk}
              okButtonLabel="CONTINUE TO REBOOK ROOM"
              cancelButtonLabel="Go back"
              event={`Trip confirmation - rebook ${itineraryForModal.tripSegmentId} segment click`}
              message={(
                <div className="gap-1 grid">
                  <div>
                    <strong>Note:</strong>
                    {' '}
                    This room was not reserved during your initial booking.
                  </div>
                  <div>
                    You&apos;ll now be taken to the hotel room rates page
                    to choose a room and complete the booking.
                    Confirmed rooms are unaffected when you rebook.
                  </div>

                  <div />
                  No cancellation fees apply, since no previous booking exists for this room.
                </div>
              )}
            />
          ) : (
            <ChangeBookingConfirmModal
              onCancel={handleCancel}
              onConfirm={handleOk}
              mode={itineraryForModal.mode}
              cancellationPolicy={itineraryForModal.cancellationPolicy}
              carrierFullName={carrierFullName}
            />
          )
        )}
      {doShowChangeModal
        && (
        <ChangeInfoModal
          onCancel={handleCancel}
          mode={selectedMode}
        />
        )}
      {doShowCancelFlightModal && (
      <CancelFlightInfoModal
        onCancel={handleCancel}
        carrierFullName={carrierFullName}
      />
      )}
      {doShowCancelModal && itineraryForModal
        && (
          <CancelBookingConfirmModal
            onCancel={handleCancel}
            onConfirm={handleOkCancellation}
            mode={itineraryForModal.mode}
            cancellationPolicy={itineraryForModal.cancellationPolicy}
            tripSegmentId={itineraryForModal.tripSegmentId}
            tripId={tripId}
          />
        )}
      {doShowCancelLoginModal && (
      <LoginRequestModal
        onCancel={() => setDoShowCancelLoginModal(false)}
        actionName="cancel booking"
      />
      )}
    </div>
  );
};

ViewTripConfigurationItems.propTypes = {
  tripId: PropTypes.string,
  isTripSearch: PropTypes.bool,
  doShowConfirmedTrip: PropTypes.bool,
  onRemoveClick: PropTypes.func.isRequired,
  updatedItineraries: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  cruiseTotals: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  onTabChange: PropTypes.func,
  doShowAgentCommission: PropTypes.bool,
  totalPriceBreakdown: PropTypes.shape({}),
};

ViewTripConfigurationItems.defaultProps = {
  tripId: null,
  isTripSearch: true,
  updatedItineraries: null,
  cruiseTotals: null,
  doShowConfirmedTrip: false,
  onTabChange: null,
  doShowAgentCommission: true,
  totalPriceBreakdown: null,
};

export default memo(ViewTripConfigurationItems);
